import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { updateMiscBill, getCategory, getAllPredefinedMiscItems } from "../../api/Api";

const EditMiscBill = ({ bill, onBack, onBillUpdated }) => {
  const [items, setItems] = useState(bill.items.map(i => ({...i, quantity: i.quantity || 1})));
  const [totalAmount, setTotalAmount] = useState(bill.totalAmount);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(bill.category._id);
  const [payeeName, setPayeeName] = useState(bill.payeeName);
  const [payeePhoneNumber, setPayeePhoneNumber] = useState(bill.payeePhoneNumber);
  const [particulars, setParticulars] = useState(bill.particulars);
  const [amountInWords, setAmountInWords] = useState(bill.amountInWords);
  const [accountantName, setAccountantName] = useState(bill.accountantName);
  const [modeOfPayment, setModeOfPayment] = useState(bill.modeOfPayment);
  const [billDate, setBillDate] = useState(bill.date ? new Date(bill.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
  const [predefinedMiscItems, setPredefinedMiscItems] = useState([]);
  const [selectedPredefinedItem, setSelectedPredefinedItem] = useState("");

  const numberToWords = (num) => {
    const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
    const teens = ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
    const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
    const getTwoDigits = (n) => {
      if (n < 10) return ones[n];
      else if (n < 20) return teens[n - 10];
      else return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "");
    };
    const getThreeDigits = (n) => {
      if (n === 0) return "";
      const hundred = Math.floor(n / 100);
      const rest = n % 100;
      return (hundred ? ones[hundred] + " hundred" + (rest ? " and " : "") : "") + getTwoDigits(rest);
    };
    if (num === 0) return "zero";
    let result = "";
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000));
    if (crore) result += getTwoDigits(crore) + " crore ";
    if (lakh) result += getTwoDigits(lakh) + " lakh ";
    if (thousand) result += getTwoDigits(thousand) + " thousand ";
    if (hundred) result += getThreeDigits(hundred);
    return result.trim();
  };

  useEffect(() => {
    const fetchData = async () => {
        try {
            const categoriesResponse = await getCategory();
            if (categoriesResponse && categoriesResponse.data) {
                setCategories(categoriesResponse.data);
            }
            const predefinedItemsResponse = await getAllPredefinedMiscItems();
            if (predefinedItemsResponse && predefinedItemsResponse.data) {
                setPredefinedMiscItems(predefinedItemsResponse.data);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            toast.error("Failed to load initial data.");
        }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setAmountInWords(numberToWords(totalAmount));
  }, [totalAmount]);

  const handleItemChange = (index, event) => {
    const { name, value } = event.target;
    const newItems = [...items];
    newItems[index][name] = name === "quantity" || name === "amount" ? parseFloat(value) || 0 : value;
    setItems(newItems);
    calculateTotal(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { description: "", amount: "", quantity: 1 }]);
  };

  const handleAddPredefinedItem = () => {
    if (selectedPredefinedItem) {
        const item = predefinedMiscItems.find(p => p._id === selectedPredefinedItem);
        if (item) {
            const newItems = [...items, { description: item.name, amount: item.amount, quantity: 1 }];
            setItems(newItems);
            calculateTotal(newItems);
        }
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    calculateTotal(newItems);
  };

  const calculateTotal = (currentItems) => {
    const total = currentItems.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0) * (parseFloat(item.quantity) || 1);
    }, 0);
    setTotalAmount(total);
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast.error("Please select a category.");
      return;
    }
    if (items.some((item) => !item.description || !item.amount)) {
      toast.error("Please ensure all item descriptions and amounts are filled.");
      return;
    }
    if (totalAmount <= 0) {
      toast.error("Total amount must be greater than zero.");
      return;
    }
    if (!payeeName || !payeePhoneNumber || !particulars || !accountantName || !amountInWords || !modeOfPayment) {
      toast.error("Please fill in all required details.");
      return;
    }

    setLoading(true);
    try {
      const billData = {
        items: items.map((item) => ({
          description: item.description,
          amount: parseFloat(item.amount),
          quantity: parseInt(item.quantity) || 1,
        })),
        totalAmount: totalAmount,
        amountInWords: amountInWords,
        accountantName: accountantName,
        category: selectedCategory,
        payeeName,
        payeePhoneNumber,
        particulars,
        modeOfPayment,
        date: billDate,
      };
      await updateMiscBill(bill._id, billData);
      toast.success("Miscellaneous bill updated successfully!");
      onBillUpdated();
      onBack();
    } catch (err) {
      console.error("Error updating miscellaneous bill:", err);
      toast.error("Failed to update miscellaneous bill.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-white rounded-xl shadow-md">
      <ToastContainer />
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Edit Miscellaneous Bill
      </h2>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-medium">Payee Name</label>
          <input
            type="text"
            value={payeeName}
            onChange={(e) => setPayeeName(e.target.value)}
            placeholder="Enter Payee Name"
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Phone Number</label>
          <input
            type="tel"
            value={payeePhoneNumber}
            onChange={(e) => setPayeePhoneNumber(e.target.value)}
            placeholder="Enter Phone Number"
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Date</label>
          <input
            type="date"
            value={billDate}
            onChange={(e) => setBillDate(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          />
        </div>
        <div className="col-span-full">
          <label className="block mb-1 font-medium">Particulars</label>
          <input
            type="text"
            value={particulars}
            onChange={(e) => setParticulars(e.target.value)}
            placeholder="Enter Particulars"
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          />
        </div>
        <div className="col-span-full">
          <label className="block mb-1 font-medium">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-full">
          <label className="block mb-1 font-medium">Mode of Payment</label>
          <select
            value={modeOfPayment}
            onChange={(e) => setModeOfPayment(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          >
            <option value="">Select Mode of Payment</option>
            <option value="Cash">Cash</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Online Transfer">Online Transfer</option>
          </select>
        </div>
        <div className="col-span-full">
          <label className="block mb-1 font-medium">Accountant Name</label>
          <input
            type="text"
            value={accountantName}
            onChange={(e) => setAccountantName(e.target.value)}
            placeholder="Enter Accountant Name"
            className="w-full border border-gray-300 rounded-lg p-2"
            required
          />
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Bill Items:</h3>
        <div className="flex gap-2 mb-4">
            <select
            value={selectedPredefinedItem}
            onChange={(e) => setSelectedPredefinedItem(e.target.value)}
            className="border border-gray-300 rounded-lg p-2 text-sm max-w-xs"
            >
            <option value="">Select predefined item</option>
            {predefinedMiscItems.map((item) => (
                <option key={item._id} value={item._id}>
                {item.name} - ₹{item.amount}
                </option>
            ))}
            </select>
            <button
            onClick={handleAddPredefinedItem}
            className="bg-green-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-600 transition"
            >
            Add
            </button>
        </div>
        {items.map((item, index) => (
          <div key={index} className="grid grid-cols-12 gap-3 mb-3">
            <input
              type="text"
              name="description"
              className="col-span-6 border border-gray-300 rounded-lg p-2"
              placeholder="Item Description"
              value={item.description}
              onChange={(e) => handleItemChange(index, e)}
            />
            <input
              type="number"
              name="amount"
              className="col-span-2 border border-gray-300 rounded-lg p-2"
              placeholder="Amount"
              value={item.amount}
              onChange={(e) => handleItemChange(index, e)}
            />
            <input
              type="number"
              name="quantity"
              className="col-span-2 border border-gray-300 rounded-lg p-2"
              placeholder="Qty"
              value={item.quantity}
              onChange={(e) => handleItemChange(index, e)}
            />
            <button
              onClick={() => handleRemoveItem(index)}
              className="col-span-2 bg-red-500 text-white px-3 py-2 rounded-lg"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddItem}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg"
        >
          Add Item
        </button>
      </div>

      <div className="flex justify-end items-center mb-4 p-4 bg-gray-100 rounded-lg">
        <p className="text-xl font-semibold">Total Amount:</p>
        <p className="text-2xl font-bold">
          ₹{totalAmount.toLocaleString()}
        </p>
      </div>

      <div className="flex items-center mb-6 p-4 bg-gray-100 rounded-lg">
        <p className="text-lg font-semibold">Amount in Words:</p>
        <p className="text-lg capitalize">{amountInWords}</p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          className="bg-green-500 text-white px-6 py-3 rounded-lg"
          disabled={loading}
        >
          {loading ? "Updating..." : "Update Bill"}
        </button>
        <button
          onClick={onBack}
          className="bg-gray-500 text-white px-6 py-3 rounded-lg ml-4"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EditMiscBill;